import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { serviceAPI, hospitalAPI, exportAPI } from '../services/api';
import Layout from '../components/Layout';

const Services = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [filters, setFilters] = useState({
    hospital_id: '',
    anesthesia_type: '',
    start_date: '',
    end_date: ''
  });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const anesthesiaTypes = [
    'General Anesthesia',
    'Regional Anesthesia',
    'Local Anesthesia',
    'Sedation',
    'Spinal Anesthesia',
    'Epidural Anesthesia'
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchServices();
  }, [filters]);

  const fetchInitialData = async () => {
    try {
      const [servicesData, hospitalsData] = await Promise.all([
        serviceAPI.getAll(),
        hospitalAPI.getAll()
      ]);
      setServices(servicesData);
      setHospitals(hospitalsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const data = await serviceAPI.getAll(filters);
      setServices(data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const clearFilters = () => {
    setFilters({
      hospital_id: '',
      anesthesia_type: '',
      start_date: '',
      end_date: ''
    });
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const result = await exportAPI.exportToExcel(filters);
      alert(`Export successful! Downloaded: ${result.filename} (${services.length} services)`);
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  const downloadFile = (filename) => {
    if (filename) {
      // Use the correct API URL from environment
      const apiUrl = process.env.REACT_APP_API_URL || 'https://anesthetist-tracker-api.kaushalvaja3.workers.dev/api';
      window.open(`${apiUrl}/files/${filename}`, '_blank');
    }
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading services...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">All Services</h1>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="btn-secondary flex items-center justify-center space-x-2 text-sm sm:text-base disabled:opacity-50"
            >
              <span>📊</span>
              <span className="hidden sm:inline">{exporting ? 'Exporting...' : 'Export'}</span>
              <span className="sm:hidden">{exporting ? 'Exporting...' : 'Export Filtered'}</span>
            </button>
            <Link to="/services/new" className="btn-primary flex items-center justify-center space-x-2 text-sm sm:text-base">
              <span>➕</span>
              <span className="hidden sm:inline">New Service</span>
              <span className="sm:hidden">Add Service</span>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="card">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-2 sm:mb-0">Filters</h2>
            <div className="flex space-x-2">
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Clear All
                </button>
              )}
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="sm:hidden text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {showMobileFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
            </div>
          </div>
          
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 ${showMobileFilters ? 'block' : 'hidden sm:grid'}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hospital
              </label>
              <select
                name="hospital_id"
                className="form-input text-sm"
                value={filters.hospital_id}
                onChange={handleFilterChange}
              >
                <option value="">All Hospitals</option>
                {hospitals.map((hospital) => (
                  <option key={hospital.id} value={hospital.id}>
                    {hospital.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Anesthesia Type
              </label>
              <select
                name="anesthesia_type"
                className="form-input text-sm"
                value={filters.anesthesia_type}
                onChange={handleFilterChange}
              >
                <option value="">All Types</option>
                {anesthesiaTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                name="start_date"
                className="form-input text-sm"
                value={filters.start_date}
                onChange={handleFilterChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                name="end_date"
                className="form-input text-sm"
                value={filters.end_date}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          
          {hasActiveFilters && (
            <div className="mt-3 text-sm text-blue-600">
              Showing filtered results ({services.length} services)
            </div>
          )}
        </div>

        {/* Services Table */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Services ({services.length})
            </h2>
          </div>

          {services.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="mb-2">
                {hasActiveFilters ? 'No services match the current filters.' : 'No services found.'}
              </p>
              {!hasActiveFilters && (
                <Link to="/services/new" className="text-primary-600 hover:text-primary-700 font-medium">
                  Add your first service →
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Desktop Table */}
              <table className="min-w-full divide-y divide-gray-200 hidden sm:table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patient
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Surgery
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hospital
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Service Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Days
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Anesthesia
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bill
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {services.map((service) => (
                    <tr key={service.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/services/${service.id}`)}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {service.patient_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {service.patient_number}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {service.surgery_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {service.hospital_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(service.service_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {service.days_of_service}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${service.amount_charged.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {service.anesthesia_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {service.bill_filename ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadFile(service.bill_filename);
                            }}
                            className="text-primary-600 hover:text-primary-700 font-medium"
                          >
                            📄 Download
                          </button>
                        ) : (
                          <span className="text-gray-400">No file</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Cards */}
              <div className="sm:hidden space-y-3">
                {services.map((service) => (
                  <div key={service.id} className="bg-gray-50 rounded-lg p-4 cursor-pointer hover:bg-gray-100" onClick={() => navigate(`/services/${service.id}`)}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-medium text-gray-900">{service.patient_name}</div>
                        <div className="text-sm text-gray-500">{service.patient_number}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-gray-900">${service.amount_charged.toLocaleString()}</div>
                        <div className="text-sm text-gray-500">{new Date(service.service_date).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div><strong>Surgery:</strong> {service.surgery_name || 'Not specified'}</div>
                      <div><strong>Hospital:</strong> {service.hospital_name}</div>
                      <div><strong>Days:</strong> {service.days_of_service}</div>
                      <div><strong>Anesthesia:</strong> {service.anesthesia_type}</div>
                      {service.bill_filename && (
                        <div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadFile(service.bill_filename);
                            }}
                            className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                          >
                            📄 Download Bill
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Services;